import React, { Component, useState } from "react";
import Epub from "epubjs";
import { IEpubViewProps, IEpubViewStyles } from "./Types";

const defaultStyles: IEpubViewStyles = {
  viewHolder: {
    position: "relative",
    height: "100%",
    width: "100%",
    paddingTop: "35px",
    paddingBottom: "20px",
    userSelect: "none",
    // marginTop: "30%",
    // top: '5%'
  },
  view: {
    height: "100%",
    userSelect: "none",
  },
};


function replaceBase(doc: Document, section: { url: string }) {
  let base: HTMLBaseElement | null;
  let head: HTMLHeadElement | null;
  let url = section.url;
  const absolute = url.indexOf("://") > -1;

  if (!doc) return;

  head = doc.querySelector("head");
  base = head?.querySelector("base") || null;

  // Add custom stylesheet
  const link = doc.createElement('link');
  link.setAttribute('rel', 'stylesheet');
  link.type = 'text/css';
  link.href = 'https://gitaseva.org/fonts/all_lang.css';
  head?.appendChild(link);
  
  // Add CSS to prevent text selection and ensure audio controls work
  const style = doc.createElement('style');
  style.textContent = `
    * {
      user-select: none !important;
      -webkit-user-select: none !important;
      -ms-user-select: none !important;
      -moz-user-select: none !important;
    }
    audio, audio * {
      user-select: auto !important;
      -webkit-user-select: auto !important;
    }
    audio {
      pointer-events: auto !important;
    }
    audio::-webkit-media-controls {
      pointer-events: auto !important;
    }
    audio::-webkit-media-controls-panel {
      pointer-events: auto !important;
    }
  `;
  head?.appendChild(style);

  if (!base && head) {
    base = doc.createElement("base");
    head.insertBefore(base, head.firstChild);
  }

  // Fix for Safari crashing if the url doesn't have an origin
  if (!absolute && window && window.location) {
    url = window.location.origin + url;
  }

  base?.setAttribute("href", url);
}

interface IEpubViewState {
  isLoaded: boolean;
  toc: any[];
}

export class EpubView extends Component<IEpubViewProps, IEpubViewState> {
  viewerRef: React.RefObject<HTMLDivElement | null>;
  location?: string | number;
  book: any;
  rendition: any;
  prevPage: any;
  nextPage: any;

  constructor(props: IEpubViewProps) {
    super(props);
    this.state = {
      isLoaded: false,
      toc: [],
    };
    this.viewerRef = React.createRef<HTMLDivElement | null>();
    this.location = props.location;
    this.book = this.rendition = this.prevPage = this.nextPage = null;
  }

  componentDidMount() {
    this.initBook();
    document.addEventListener("keyup", this.handleKeyPress, false);
  }

  initBook() {
    const { url, tocChanged, epubInitOptions = {} } = this.props;
    if (this.book) {
      this.book.destroy();
    }
    this.book = Epub(url as string, epubInitOptions);
    this.book.loaded.navigation.then(({ toc }: any) => {
      this.setState(
        {
          isLoaded: true,
          toc: [toc],
        },
        () => {
          tocChanged && tocChanged(toc);
          this.initReader();
        }
      );
    });
  }

  componentWillUnmount() {
    this.book = this.rendition = this.prevPage = this.nextPage = null;
    document.removeEventListener("keyup", this.handleKeyPress, false);
  }

  shouldComponentUpdate(nextProps: IEpubViewProps) {
    return (
      !this.state.isLoaded ||
      nextProps.location !== this.props.location ||
      nextProps.location !== this.props.location
    );
  }

  componentDidUpdate(prevProps: IEpubViewProps) {
    if (
      prevProps.location !== this.props.location &&
      this.location !== this.props.location
    ) {
      this.rendition?.display(this.props.location);
    }
    if (prevProps.url !== this.props.url) {
      this.initBook();
    }
  }

  initReader() {
    const { toc } = this.state;
    const { location, epubOptions = {}, getRendition } = this.props;
    const node = this.viewerRef.current;
    this.rendition = this.book.renderTo(node, {
      contained: true,
      width: "100%",
      height: "100%",
      allowScriptedContent: true,
      sandbox: "allow-same-origin allow-scripts allow-forms allow-presentation",
      ...epubOptions,
    });

    this.prevPage = () => {
      this.rendition.prev();
    };
    this.nextPage = () => {
      this.rendition.next();
    };

    this.rendition.on("rendered", (section: any) => {
      const iframe = node?.querySelector("iframe");
      const doc = iframe?.contentDocument;
      if (doc) {
        replaceBase(doc, { url: section.url });
        
        // Ensure iframe allows media and scripts
        if (iframe) {
          iframe.setAttribute('allow', 'autoplay; encrypted-media');
          iframe.style.pointerEvents = 'auto';
        }
        
        // Make audio elements fully interactive
        const audioElements = doc.querySelectorAll('audio');
        audioElements.forEach((audio: HTMLAudioElement) => {
          // Remove autoplay and ensure proper settings
          audio.removeAttribute('autoplay');
          audio.setAttribute('preload', 'metadata');
          audio.style.pointerEvents = 'auto';
          
          // Enable all interaction
          audio.addEventListener('click', (e) => {
            e.stopPropagation();
          });
          
          // Force native controls to work
          audio.addEventListener('play', (e) => {
            console.log('Audio playing:', audio.currentSrc);
          });
          
          audio.addEventListener('pause', (e) => {
            console.log('Audio paused:', audio.currentSrc);
          });
          
          // Prevent parent elements from blocking events
          audio.addEventListener('mousedown', (e) => {
            e.stopPropagation();
          });
          
          audio.addEventListener('mouseup', (e) => {
            e.stopPropagation();
          });
          
          console.log('Audio element found and enabled:', audio.currentSrc || audio.src);
        });
        
        // Ensure document allows interactions
        if (doc.body) {
          doc.body.style.pointerEvents = 'auto';
        }
      }
    });


    this.registerEvents();
    getRendition && getRendition(this.rendition);

    if (typeof location === "string" || typeof location === "number") {
      this.rendition.display(location);
    } else if (toc.length > 0 && toc[0].href) {
      this.rendition.display(toc[0].href);
    } else {
      this.rendition.display();
    }
  }

  registerEvents() {
    const { handleKeyPress, handleTextSelected } = this.props;
    this.rendition.on("locationChanged", this.onLocationChange);
    this.rendition.on("keyup", handleKeyPress || this.handleKeyPress);
    if (handleTextSelected) {
      this.rendition.on("selected", handleTextSelected);
    }
  }

  onLocationChange = (loc: any) => {
    const { location, locationChanged } = this.props;
    const newLocation = loc && loc.start;
    if (location !== newLocation) {
      console.log("Location does not changed, forcing to update to ", newLocation);
      this.location = newLocation;
      locationChanged && locationChanged(newLocation);
    }
  };

  renderBook() {
    const { styles = defaultStyles } = this.props;
    return <div ref={this.viewerRef} style={styles.view} />;
  }

  handleKeyPress = ({ key }: any) => {
    key && key === "ArrowRight" && this.nextPage();
    key && key === "ArrowLeft" && this.prevPage();
  };

  render() {
    const { isLoaded } = this.state;
    const { loadingView, styles = defaultStyles } = this.props;
    return (
      <div style={styles.viewHolder}>
        {(isLoaded && this.renderBook()) || loadingView}
      </div>
    );
  }
}
